import type { TFunction } from 'i18next';

import { parseApiError } from '@saas-kit/lib';
import { toast } from '@saas-kit/ui';
import type { BulkAction } from '@saas-kit/ui/DataTable';

import {
  archiveTenant,
  reactivateTenant,
  suspendTenant,
  type AdminTenant,
} from './api';

interface Deps {
  t: TFunction;
  invalidate: () => Promise<void>;
}

async function runBulk(
  rows: AdminTenant[],
  fn: (id: string) => Promise<AdminTenant>,
  invalidate: () => Promise<void>,
  successMessage: string,
): Promise<void> {
  if (rows.length === 0) return;
  try {
    await Promise.all(rows.map((r) => fn(r.id)));
    await invalidate();
    toast.success(successMessage);
  } catch (err) {
    toast.error(parseApiError(err).message);
  }
}

export function buildTenantBulkActions({ t, invalidate }: Deps): BulkAction<AdminTenant>[] {
  return [
    {
      id: 'suspend',
      label: t('actions.suspend'),
      variant: 'secondary',
      isDisabled: (rows) =>
        rows.some((r) => r.status === 'active') ? null : t('bulk.no_active'),
      onRun: (rows) =>
        runBulk(
          rows.filter((r) => r.status === 'active'),
          suspendTenant,
          invalidate,
          t('toasts.bulk_suspended', { count: rows.length }),
        ),
    },
    {
      id: 'reactivate',
      label: t('actions.reactivate'),
      variant: 'secondary',
      isDisabled: (rows) =>
        rows.some((r) => r.status === 'suspended') ? null : t('bulk.no_suspended'),
      onRun: (rows) =>
        runBulk(
          rows.filter((r) => r.status === 'suspended'),
          reactivateTenant,
          invalidate,
          t('toasts.bulk_reactivated', { count: rows.length }),
        ),
    },
    {
      id: 'archive',
      label: t('actions.archive'),
      variant: 'destructive',
      isDisabled: (rows) =>
        rows.some((r) => r.status !== 'archived') ? null : t('bulk.no_archivable'),
      onRun: (rows) =>
        runBulk(
          rows.filter((r) => r.status !== 'archived'),
          archiveTenant,
          invalidate,
          t('toasts.bulk_archived', { count: rows.length }),
        ),
    },
  ];
}
